import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles.css';
import ExpenseForm from './ExpenseForm';
import Header from './Header';
import Swal from 'sweetalert2';

function EditExpense({ handleLogout, goToHome }) {
  const { noteId, expenseId } = useParams();
  const navigate = useNavigate();
  const formId = `edit-expense-form-${expenseId}`;
  const [isLoading, setIsLoading] = useState(true);
  const [expense, setExpense] = useState(null);
  const [note, setNote] = useState(null);
  const handleSubmitClick = () => {
    document.getElementById(formId)?.requestSubmit();
  };

  // Charger les données de la dépense et de la note
  useEffect(() => {
    const fetchExpenseAndNote = async () => {
      try {
        // Vérifier le statut de la note
        const noteResponse = await fetch(`/api/get-note/${noteId}`, {
          method: 'GET',
          credentials: 'include'
        });

        if (!noteResponse.ok) {
          throw new Error('Erreur lors de la récupération de la note');
        }

        const noteData = await noteResponse.json();
        setNote(noteData);

        if (noteData.statut === 'payee') {
          Swal.fire({
            icon: 'warning',
            text: 'Vous ne pouvez pas modifier les dépenses d\'une note de frais déjà traitée.',
            confirmButtonText: 'OK'
          }).then(() => {
            navigate(`/get-expenses/${noteId}`);
          });
          return;
        }

        // Récupérer les dépenses pour trouver celle à modifier
        const expensesResponse = await fetch(`/api/get-expenses/${noteId}`, {
          method: 'GET',
          credentials: 'include'
        });

        if (!expensesResponse.ok) {
          throw new Error('Erreur lors de la récupération des dépenses');
        }

        const expensesData = await expensesResponse.json();
        const expenseToEdit = expensesData.find(exp => exp.id_depense === parseInt(expenseId));

        if (!expenseToEdit) {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: 'Dépense non trouvée.',
            confirmButtonText: 'OK'
          }).then(() => {
            navigate(`/get-expenses/${noteId}`);
          });
          return;
        }

        setExpense(expenseToEdit);
      } catch (error) {
        console.error('Erreur:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Une erreur est survenue lors du chargement des données.',
          confirmButtonText: 'OK'
        }).then(() => {
          navigate(`/get-expenses/${noteId}`);
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchExpenseAndNote();
  }, [noteId, expenseId, navigate]);

  const handleUpdateExpense = async (formData) => {
    setIsLoading(true);
    
    try {
      Swal.fire({
        title: 'En cours...',
        text: 'Mise à jour de la dépense en cours, veuillez patienter.',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Ajuster les noms des champs pour correspondre à l'API
      if (formData.has('existingFiles')) {
        const existingFiles = formData.get('existingFiles');
        formData.delete('existingFiles');
        formData.append('justificatifsExistants', existingFiles);
      }

      const response = await fetch(`/api/update-expense/${expenseId}`, {
        method: 'PUT',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour de la dépense');
      }
      Swal.close();

      navigate(`/get-expenses/${noteId}`);
    } catch (error) {
      console.error('Erreur:', error);
      Swal.fire({
        title: 'Erreur !',
        text: 'Une erreur est survenue lors de la mise à jour de la dépense.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    navigate(`/get-expenses/${noteId}`);
  };

  if (!expense || !note) {
    return null; // n'affiche rien tant que ce n'est pas prêt
  }

  // Déterminer le type de dépense
  const expenseType = expense.type_depense || 'other';

  return (
    <div>
      <main className="add-expense-container">
        <ExpenseForm
          expenseType={expenseType}
          noteId={noteId}
          onSave={handleUpdateExpense}
          onCancel={handleCancelEdit}
          existingExpense={expense}
          formId={formId}
          showSubmitButton={false}
        />
      </main>

      <footer>
        <Header handleLogout={handleLogout} />
        <button
          type="button"
          className="footer-center-button footer-submit-button"
          aria-label="Modifier la dépense"
          title="Modifier la dépense"
          onClick={handleSubmitClick}
          disabled={isLoading}
        >
          <i className="fa-solid fa-check"></i>
        </button>
        <button
          type="button"
          className="footer-side-right-button"
          onClick={() => navigate(`/get-expenses/${noteId}`)}
          aria-label="Annuler et revenir"
          title="Annuler et revenir"
          disabled={isLoading}
        >
          <i className="fa-solid fa-xmark"></i>
        </button>
      </footer>
    </div>
  );
}

export default EditExpense; 
